<template>
  <div class="convey911-admin-dashboard px-2 px-md-4 py-2 py-md-4">
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
      <h4 class="convey911-admin-dashboard--page-title mb-0">Admin Dashboard</h4>
      <ConveyDatePicker
        placeholder="Select Date Range"
        :isDarkMode="isDarkMode"
        :currentDateRange="dateRange"
        @changeDatePicker="handleChangeDatePicker"
      />
    </div>

    <b-overlay
      :show="isLoading || isConversationLoading || isGeoMapLoading"
      rounded="sm"
      no-wrap
    ></b-overlay>
    <div class="convey911-admin-dashboard--blocks row d-flex">
      <div class="col-md-12 col-xl-6 convey911-admin-dashboard--statistics-div">
        <Statistics :statsCount="statsCount" :isDarkMode="isDarkMode" />
      </div>
      <div
        class="col-md-12 col-xl-6 mb-3 convey911-admin-dashboard--conversation-graph-div"
        style="height: 388px"
      >
        <ConversationsGraph
          :conversationActivityData="conversationActivityData"
          :isDarkMode="isDarkMode"
        />
      </div>
    </div>
    <div class="convey911-admin-dashboard--blocks row d-flex">
      <div class="col-md-12 col-sm-12 col-lg-6 col-xl-3 mb-3">
        <div class="d-flex flex-column gap-3">
          <div style="height: 388px">
            <RecentActivities
              :recentActivitiesData="recentActivitiesData"
              :isDarkMode="isDarkMode"
            />
          </div>
          <div style="height: 210px; min-height: 210px">
            <GeolocationActivity
              :geolocationActivityData="geolocationActivityData"
              :isDarkMode="isDarkMode"
            />
          </div>
        </div>
      </div>

      <div class="col-md-12 col-sm-12 col-lg-6 col-xl-3 mb-3">
        <TopLanguages
          :topLanguagesData="topLanguagesData"
          :isDarkMode="isDarkMode"
        />
      </div>

      <div class="col-md-12 col-sm-12 col-lg-12 col-xl-6 mb-3">
        <RecentConversations
          :recentConversationsData="recentConversationsData"
          :isDarkMode="isDarkMode"
        />
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Statistics from "./Statistics.vue";
import ConversationsGraph from "./ConversationsGraph.vue";
import RecentConversations from "./RecentConversations.vue";
import TopLanguages from "./TopLanguages.vue";
import RecentActivities from "./RecentActivities.vue";
import GeolocationActivity from "../../geolocation-activity/GeolocationActivity.vue";
import ConveyDatePicker from "../../common/DatePicker.vue";
import moment from "moment";
import { mapGetters } from "vuex";
import { runGraphqlQuery } from "../../../graphql/utils";
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";
import {
  makeRecentConversationQuery,
  makeAdminStatsQuery,
  makeAdminGeoLocationStatsQuery,
} from "../../../graphql/queries";

export default {
  name: "AdminDashboard",
  mixins: [formatter, errorHandler],
  components: {
    Statistics,
    ConversationsGraph,
    RecentConversations,
    TopLanguages,
    RecentActivities,
    GeolocationActivity,
    ConveyDatePicker,
  },
  data() {
    return {
      dateRange: {
        start_date: moment().subtract(29, "days").format("YYYY-MM-DD"),
        end_date: moment().format("YYYY-MM-DD"),
      },
      statsCount: {
        activeUsers: null,
        invitations: null,
        messages: null,
        translationsCount: 0,
        locationRequest: null,
        videoSessions: null,
      },
      recentActivitiesData: [],
      conversationActivityData: null,
      geolocationActivityData: null,
      recentConversationsData: [],
      topLanguagesData: null,
      isLoading: false,
      isGeoMapLoading: false,
      isConversationLoading: false,
      fetchAbortConroller: null,
    };
  },
  computed: {
    ...mapGetters("user", ["currentToken", "isDarkMode"]),
    ...mapGetters("agency", ["currentAgencyId"]),
  },
  created() {
    this.fetchOperations();
  },
  watch: {
    dateRange(newVal, oldVal) {
      if (newVal != oldVal) {
        this.fetchOperations();
      }
    },
  },
  methods: {
    fetchOperations() {
      this.fetchAbortConroller = new AbortController();
      this.fetchAllData();
      this.getRecentConversation();
      this.fetchGeoLocationData();
    },
    abortFetchOperations() {
      if (this.fetchAbortConroller) {
        this.fetchAbortConroller.abort();
        this.fetchAbortConroller = null;
      }
    },
    handleChangeDatePicker(data) {
      if (data.start && data.end) {
        this.dateRange = {
          start_date: data.start,
          end_date: data.end,
        };
      } else {
        this.dateRange = null;
      }
    },
    async fetchAllData() {
      try {
        this.isLoading = true;
        let startDate = moment().startOf("year").format("YYYY-MM-DD");
        let endDate = moment().endOf("year").format("YYYY-MM-DD");

        if (this.dateRange && this.dateRange.start_date) {
          startDate = this.dateRange.start_date;
          endDate = this.dateRange.end_date;
        }

        const query = makeAdminStatsQuery(startDate, endDate);
        const res = await runGraphqlQuery(query, this.fetchAbortConroller.signal);
        const { getAdminStats } = res.data.data;
        const responseData = getAdminStats;

        this.statsCount = {
          activeUsers: {
            totalCount: this.formatNumber(
              responseData.agencyUserStats.totalCount
            ),
            adminCount: this.formatNumber(
              responseData.agencyUserStats.adminCount
            ),
            operatorCount: this.formatNumber(
              responseData.agencyUserStats.operatorCount
            ),
            otherCount: this.formatNumber(
              responseData.agencyUserStats.otherCount
            ),
          },
          invitations: {
            acceptedCount: this.formatNumber(
              responseData.invitations.accepted
            ),
            pendingCount: this.formatNumber(responseData.invitations.pending),
            totalCount: this.formatNumber(
              responseData.invitations.pending +
                responseData.invitations.accepted
            ),
          },
          translationsCount: this.formatNumber(
            responseData.translationsCount
          ),
          messages: {
            totalCount: this.formatNumber(
              responseData.messageCount.totalCount
            ),
            mmsCount: this.formatNumber(responseData.messageCount.mmsCount),
            smsCount: this.formatNumber(responseData.messageCount.smsCount),
            othersCount: this.formatNumber(
              responseData.messageCount.othersCount
            ),
          },
          locationRequest: {
            count: this.formatNumber(
              responseData.geolocationRequestCount.count
            ),
            graphData: responseData.geolocationRequestCount.graphData,
          },
          videoSessions: {
            count: this.formatNumber(responseData.videoSessions.count),
            average: this.formatDuration(responseData.videoSessions.average),
            graphData: responseData.videoSessions.graphData,
          },
        };
        const avgConvActivityDurationMin = Math.floor(
          responseData.conversationGraphStats.averageDuration / 60
        );
        const avgConvActivityDurationSec = Math.floor(
          responseData.conversationGraphStats.averageDuration % 60
        );
        this.conversationActivityData = {
          ...responseData.conversationGraphStats,
          conversationCount: this.formatNumber(
            responseData.conversationGraphStats.conversationCount
          ),
          averageDuration: `${avgConvActivityDurationMin}m ${avgConvActivityDurationSec}s`,
        };
        this.recentActivitiesData = responseData.recentActivity;
        this.topLanguagesData = responseData.translationBreakdownStat;
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[AdminDashboard:fetchAllData] ERROR: ", err);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async getRecentConversation() {
      try {
        this.isConversationLoading = true;
        let startDate = moment().startOf("year").format("YYYY-MM-DD");
        let endDate = moment().endOf("year").format("YYYY-MM-DD");

        if (this.dateRange && this.dateRange.start_date) {
          startDate = this.dateRange.start_date;
          endDate = this.dateRange.end_date;
        }

        const query = makeRecentConversationQuery(startDate, endDate);
        const res = await runGraphqlQuery(query, this.fetchAbortConroller.signal);
        const responseData = res.data.data.getConversations;
        this.recentConversationsData = responseData.data;
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[AdminDashboard:getRecentConversation] ERROR: ", err);
        }
      } finally {
        this.isConversationLoading = false;
      }
    },
    async fetchGeoLocationData() {
      try {
        this.isGeoMapLoading = true;
        let startDate = moment().startOf("year").format("YYYY-MM-DD");
        let endDate = moment().endOf("year").format("YYYY-MM-DD");

        if (this.dateRange && this.dateRange.start_date) {
          startDate = this.dateRange.start_date;
          endDate = this.dateRange.end_date;
        }

        const query = makeAdminGeoLocationStatsQuery(startDate, endDate);
        const res = await runGraphqlQuery(query, this.fetchAbortConroller.signal);
        const { getAdminStats } = res.data.data;
        const responseData = getAdminStats;
        this.geolocationActivityData = responseData.geolocationActivity;
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[AdminDashboard:fetchGeoLocationData] ERROR: ", err);
        }
      } finally {
        this.isGeoMapLoading = false;  
      }
    },
  },
  beforeDestroy() {
    this.abortFetchOperations();
  }
};
</script>

<style lang="scss">
.convey911-admin-dashboard {
  &--blocks {
    &.row {
      margin: 0 -0.5rem !important;
      > * {
        padding: 0 0.5rem !important;
      }
    }
  }
}
</style>
